import { createNamespacedHelpers } from 'vuex'
import featureMixin from './featureMixin'
import permissionMixin from './permissionMixin'
import { message, notification } from 'ant-design-vue'

const { mapActions: projectActions } = createNamespacedHelpers('project')

export default {
  name: 'RoutesMixin',
  data() {
    return {}
  },
  mixins: [featureMixin, permissionMixin],
  computed: {
    currentPaymentTypeId() {
      return this.$store.state.applicationState.currentPaymentType
    },
    externalServices() {
      return this.$store.state.applicationState.currentProject?.external_services ?? []
    },

    routes() {
      if (!this.hasAdminGroup && !this.hasSupervisorGroup) {
        return [
          {
            id: 'kpi-tab',
            title: 'Dashboard',
            icon: 'ri:dashboard-fill',
            visible: true,
            key: 'kpi'
          }
        ]
      } else if (!this.hasAdminGroup && this.hasSupervisorGroup) {
        return [
          {
            id: 'kpi-tab',
            title: 'Dashboard',
            icon: 'ri:dashboard-fill',
            visible: true,
            key: 'kpi'
          },
          {
            id: 'workforce-management-submenu',
            title: 'Workforce Management',
            icon: 'fluent:shifts-team-20-filled',
            visible: true,
            nestedRoutes: [
              {
                key: 'schedule',
                id: 'schedule-tab',
                title: 'Schedule',
                icon: 'uis:schedule',
                ...this.externalRoutes('schedule', 'uis:schedule')
              },
              {
                key: 'marketplace',
                id: 'marketplace-tab',
                title: 'Marketplace',
                icon: 'material-symbols:local-convenience-store',
                ...this.externalRoutes('marketplace', 'material-symbols:local-convenience-store')
              },
              {
                key: 'assignment',
                id: 'assignment-tab',
                title: 'Assignment',
                icon: 'solar:notebook-bold',
                ...this.externalRoutes('assignment', 'solar:notebook-bold')
              },
              {
                key: 'adl',
                id: 'adl-tab',
                title: 'Adl',
                icon: 'streamline-plump:task-list-edit-solid',
                ...this.externalRoutes('adl', 'streamline-plump:task-list-edit-solid')
              }
            ]
          }
        ]
      }

      return [
        {
          key: 'dashboard',
          id: 'company-home-tab',
          title: 'Dashboard',
          icon: 'ri:dashboard-fill',
          visible: true
        },
        {
          id: 'workforce-management-submenu',
          title: 'Workforce Management',
          icon: 'fluent:shifts-team-20-filled',
          visible: true,
          nestedRoutes: [
            {
              key: 'shifts',
              id: 'associate-shifts-tab',
              title: 'Shifts',
              icon: 'fluent:shifts-20-filled',
              visible: this.hasFeature('view_shifts') && this.hasPermission('manage_shifts')
            },
            {
              key: 'project-shift-auths',
              id: 'associate-auths-tab',
              title: 'Violations',
              icon: 'fluent:shifts-deny-24-filled',
              visible: this.hasFeature('authorization')
            },
            {
              key: 'time-off',
              id: 'associate-time-off-tab',
              title: 'Time Offs',
              icon: 'icon-park-solid:vacation',
              visible: this.hasFeature('time_off') && this.hasPermission('manage_shifts')
            },
            {
              key: 'uncompleted-critical-tasks',
              id: 'uncompleted-critical-tasks-tab',
              title: 'Uncompleted Task',
              icon: 'streamline-plump:warning-diamond-solid',
              visible: this.hasPermission('manage_shifts')
            },
            {
              key: 'activity',
              id: 'associate-activity-tab',
              title: 'Activity',
              icon: 'streamline-plump:office-worker-solid',
              visible: this.hasFeature('view_shifts') && this.hasPermission('manage_shifts')
            },
            {
              key: 'daily-summary',
              id: 'reports-daily-summary-tab',
              title: 'Daily Summary',
              icon: 'mdi:report-box',
              visible: this.hasFeature('view_shifts') && this.hasPermission('manage_shifts')
            },
            {
              key: 'schedule',
              id: 'schedule-tab',
              title: 'Schedule',
              icon: 'uis:schedule',
              ...this.externalRoutes('schedule', 'uis:schedule')
            },
            {
              key: 'marketplace',
              id: 'marketplace-tab',
              title: 'Marketplace',
              icon: 'material-symbols:local-convenience-store',
              ...this.externalRoutes('marketplace', 'material-symbols:local-convenience-store')
            },
            {
              key: 'assignment',
              id: 'assignment-tab',
              title: 'Assignment',
              icon: 'solar:notebook-bold',
              ...this.externalRoutes('assignment', 'solar:notebook-bold')
            },
            {
              key: 'adl',
              id: 'adl-tab',
              title: 'Adl',
              icon: 'streamline-plump:task-list-edit-solid',
              ...this.externalRoutes('adl', 'streamline-plump:task-list-edit-solid')
            }
          ]
        },
        {
          id: 'resources-submenu',
          title: 'Human Resources',
          icon: 'streamline-ultimate:human-resources-workflow-bold',
          visible: true,
          nestedRoutes: [
            {
              key: 'associate-profiles',
              id: 'associate-profiles-tab',
              title: 'Personnel Information',
              icon: 'iconamoon:profile-circle-fill',
              visible: this.hasPermission('manage_associates')
            },
            {
              key: 'invite-history',
              id: 'invite-history-tab',
              title: 'Invitations',
              icon: 'fluent:mail-20-filled',
              visible: true
            },
            {
              key: 'legal-entity',
              id: 'legal-entity-tab',
              title: 'Legal Entities',
              icon: 'fluent:building-people-24-filled',
              visible: this.userPermissions.some(perm => perm.name === 'manage_associates' && perm.scope === 'all')
            },
            {
              key: 'documentation-page',
              id: 'documentation-page-tab',
              title: 'Doc. Library',
              icon: 'fluent:document-ribbon-16-filled',
              visible: this.hasFeature('document') && this.hasPermission('manage_documents')
            },
            {
              key: 'trainings-page',
              id: 'trainings-page-tab',
              title: 'Trainings',
              icon: 'mdi:book-education',
              visible: this.hasFeature('document') && this.hasPermission('manage_documents')
            },
            {
              key: 'complete-request',
              id: 'complete-request-tab',
              title: 'Doc. Requests',
              icon: 'solar:document-add-bold',
              visible: this.hasFeature('document') && this.hasPermission('manage_documents')
            },
            {
              key: 'visitors',
              id: 'visitors',
              title: 'Visitors',
              icon: 'fluent:guest-32-filled',
              visible: this.hasFeature('visitor')
            }
          ]
        },
        {
          id: 'clients-submenu',
          title: 'Clients',
          icon: 'fluent:building-retail-money-24-filled',
          visible: this.$store.state.applicationState.currentProject.staffing,
          nestedRoutes: [
            {
              key: 'clients',
              id: 'clients-tab',
              title: 'Clients',
              icon: 'fluent:building-retail-money-24-filled',
              visible: true
            },
            {
              key: 'clients-request',
              id: 'clients-request-tab',
              title: 'Client Requests',
              icon: 'fa6-solid:file-invoice-dollar',
              visible: this.hasFeature('payment') && this.hasAnyPermission(['validate_payments', 'process_payments'])
            },

            {
              key: 'clients-request-history',
              id: 'clients-request-history-tab',
              title: 'Payment History',
              icon: 'material-symbols:request-quote-rounded',
              visible: this.hasFeature('payment') && this.hasAnyPermission(['validate_payments', 'process_payments'])
            }
          ]
        },
        {
          id: 'messaging-submenu',
          title: 'Messaging',
          icon: 'solar:plain-bold',
          visible: true,
          nestedRoutes: [
            {
              key: 'notifications',
              id: 'notification-notif-tab',
              title: 'Notification',
              icon: 'streamline-plump:mail-notification-solid',
              visible: this.hasPermission('send_push_notification')
            },
            {
              key: 'chats',
              id: 'chats-tab',
              title: 'Chats',
              icon: 'mynaui:message-dots-solid',
              visible: this.hasFeature('chat')
            }
          ]
        },
        {
          id: 'payments-submenu',
          title: 'Payments',
          icon: 'streamline:payment-10-solid',
          visible: this.hasFeature('payment') && this.hasAnyPermission(['validate_payments', 'process_payments']),
          nestedRoutes: [
            {
              key: 'invoices',
              id: 'invoices-tab',
              title: 'Contractor Invoices',
              icon: 'fa6-solid:file-invoice-dollar',
              visible: !this.currentPaymentTypeId || this.currentPaymentTypeId === 6
            },
            {
              key: 'advances',
              id: 'advances-tab',
              title: 'Employee Advances',
              icon: 'bitcoin-icons:invoice-filled',
              visible: !this.currentPaymentTypeId || this.currentPaymentTypeId === 3
            },
            {
              key: 'company-invoice',
              id: 'company-invoice-tab',
              title: 'Company Invoices',
              icon: 'fa6-solid:file-invoice-dollar',
              visible: this.hasFeature('payment') && this.hasAnyPermission(['validate_payments', 'process_payments'])
            },
            {
              key: 'past-payrolls',
              id: 'past-payrolls-tab',
              title: 'Payroll',
              icon: 'streamline-ultimate:cash-payment-sign-2-bold',
              visible: !this.currentPaymentTypeId || this.currentPaymentTypeId === 3
            },
            {
              key: 'payments',
              id: 'payments-tab',
              title: 'Payment History',
              icon: 'material-symbols:request-quote-rounded',
              visible: true
            }
          ]
        },
        {
          id: 'reports-submenu',
          title: 'Reports',
          icon: 'mdi:report-box-multiple',
          visible: this.hasFeature('view_shifts'),
          nestedRoutes: [
            ...this.transformReportData(this.$store.state.applicationState.reports),
            {
              key: 'time-attendance-detail-report',
              id: 'reports-time-attendance-tab',
              title: 'Time & Attendance',
              icon: 'fluent:shifts-20-filled',
              visible: true
            },
            {
              key: 'documentation-compliance-report',
              id: 'reports-documentation-compliance-tab',
              title: 'Documentation',
              icon: 'solar:documents-bold',
              visible: true
            },
            {
              key: 'shift-card-report',
              id: 'reports-shift-card-tab',
              title: 'Shift Time Card',
              icon: 'fluent:document-text-clock-24-filled',
              visible: true
            },
            {
              key: 'unplanned-cost-report',
              id: 'reports-unplanned-cost-tab',
              title: 'Unplanned Cost',
              icon: 'fluent:money-calculator-20-filled',
              visible: true
            },
            {
              key: 'high-risk-report',
              id: 'reports-high-risk-tab',
              title: 'High Risk Associates',
              icon: 'fluent:person-error-20-filled',
              visible: true
            },
            {
              key: 'cross-system-report',
              id: 'reports-cross-system-tab',
              title: 'Cross-System',
              icon: 'material-symbols:folder-data-rounded',
              visible: true
            },
            {
              key: 'pbj-report',
              id: 'reports-pbj-tab',
              title: 'PBJ & PPD',
              icon: 'si:fact-check-fill',
              condition: 'currentProjectIsSfhcr',
              visible: true
            },
            {
              key: 'audit-report',
              id: 'reports-audit-tab',
              title: 'Audit',
              icon: 'solar:folder-check-bold',
              visible: true
            },
            {
              key: 'discrepancy-report',
              id: 'reports-discrepancy-tab',
              title: 'Discrepancy',
              icon: 'material-symbols:folder-data-rounded',
              visible: true
            },
            {
              key: 'total-hours-report',
              id: 'total-hours-report-tab',
              title: 'Total Hours',
              icon: 'fluent:shifts-question-mark-24-filled',
              visible: true
            }
          ]
        },
        {
          key: 'configuration-page',
          id: 'configuration-page-tab',
          title: 'Settings',
          icon: 'solar:settings-bold',
          visible: this.hasPermission('manage_project')
        }
      ]
    },

    visibleRoutes() {
      const filterVisible = routes => {
        return routes.reduce((acc, item) => {
          const hasNested = item.nestedRoutes && item.nestedRoutes.length > 0
          const filteredNested = hasNested ? filterVisible(item.nestedRoutes) : []

          if (item.visible) {
            acc.push({
              ...item,
              nestedRoutes: filteredNested
            })
          }

          return acc
        }, [])
      }

      return filterVisible(this.routes)
    }
  },
  methods: {
    ...projectActions(['getSchedulerLink']),

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    transformReportData(data) {
      const result = data.map(item => {
        let result = []

        if (item.items && item.items.length > 0) {
          const itemsRoutes = item.items.map(subItem => ({
            key: `export-service/${subItem.endpoint_name}`,
            id: `export-service${subItem.endpoint_name}-tab`,
            title: subItem.name,
            icon: 'mdi:report-box-multiple',
            visible: true
          }))

          result = [...itemsRoutes]
        }

        return result
      })

      return result.flat()
    },

    externalRoutes(type, icon) {
      const nested = this.externalServices
        .filter(item => item.name === type)
        .map((item, idx) => ({
          key: `${item.name}_${item.id}`,
          id: item.id,
          title: item.label ?? `${item.name} ${idx + 1}`,
          icon,
          onClick: async () => {
            let loadingMessage = null

            try {
              let url

              if (type === 'marketplace') {
                url = item.url
              } else {
                loadingMessage = message.loading('Loading..', 0)

                url = await this.getSchedulerLink({
                  target_url: item.url
                })
              }

              window.open(url, '_blank')
            } catch (error) {
              this.showNotification('error', 'Error', error?.message ?? String(error))
            } finally {
              if (loadingMessage !== null && loadingMessage()) {
                loadingMessage().destroy()
              }
              this.loading = false
            }
          },
          visible: true
        }))

      return nested.length > 1
        ? {
            nestedRoutes: nested,
            visible: type === 'marketplace' ? this.hasPermission('manage_marketplace') : true
          }
        : {
            ...(nested.length ? nested[0] : {}),
            visible:
              type === 'marketplace' ? !!nested.length && this.hasPermission('manage_marketplace') : !!nested.length
          }
    }
  }
}
